import React, { useState } from "react";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { trackClick } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POINTS = [
  { t: "Pan-India Roadmap", d: "A measured, state-by-state expansion plan." },
  { t: "Exclusive Territories", d: "Protected geographies for committed partners." },
  { t: "Strong Margins", d: "A distributor margin structure built to scale." },
  { t: "Marketing Lift", d: "Co-branded festival campaigns and trade visibility." },
  { t: "Supply Reliability", d: "Steady fulfilment, especially during the festival peak." },
  { t: "Growth Support", d: "A dedicated regional manager to grow your territory." },
];

const Distributor = () => {
  const [form, setForm] = useState({ name: "", company: "", state: "", phone: "", territory: "" });
  const onSubmit = async (e) => {
    e.preventDefault();
    trackClick("distributor-enquiry-submit");
    try {
      await axios.post(`${API}/leads`, {
        kind: "distributor",
        name: form.name,
        company: form.company,
        location: form.state,
        phone: form.phone,
        territory: form.territory,
      });
      toast.success("Thank you — our distribution head will reach out personally.");
      setForm({ name: "", company: "", state: "", phone: "", territory: "" });
    } catch (_) {
      toast.error("Could not submit — please try again or email us.");
    }
  };

  return (
    <div data-testid="distributor-page">
      <SEO
        title="Sanatan Dharma Distributor Programme"
        description="Own a state territory. Distributor programme for Sanatan Dharma premium puja essentials — pan-India rollout, exclusive geographies, strong margins."
        keywords="sanatan dharma distributor, puja product distributor india, state territory dealership, wholesale supplier puja items"
        path="/distributor"
      />
      <section className="mandala-bg border-b border-[#3B2A1A]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <p className="overline mb-5">Distributor Programme</p>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.98] text-[#3B2A1A] tracking-tight">
              Build The Future <br/><span className="italic text-[#C89D3C] font-light">Of Ritual Essentials.</span>
            </h1>
          </div>
          <div className="lg:col-span-5 text-lg leading-relaxed text-[#5C4A3A] font-light">
            <p>An invitation to the rare, ambitious distributors who see ritual essentials as a serious household category — and want to own it in their state.</p>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading overline="The Opportunity" title="Five states, year one. Twenty by year three." subtitle="A clear, measured rollout plan — built on real distributor partnerships, not press releases." />
            <div className="mt-10 space-y-6 max-w-md">
              <div className="flex items-start gap-5" data-testid="dist-phase-1">
                <p className="font-serif-display text-3xl text-[#C89D3C] leading-none">01</p>
                <div>
                  <p className="font-serif-display text-xl text-[#3B2A1A]">North & Central India</p>
                  <p className="text-sm text-[#5C4A3A] mt-1 font-light">UP, Bihar, MP, Rajasthan, Delhi NCR.</p>
                </div>
              </div>
              <div className="flex items-start gap-5" data-testid="dist-phase-2">
                <p className="font-serif-display text-3xl text-[#C89D3C] leading-none">02</p>
                <div>
                  <p className="font-serif-display text-xl text-[#3B2A1A]">West & East</p>
                  <p className="text-sm text-[#5C4A3A] mt-1 font-light">Gujarat, Maharashtra, West Bengal, Odisha.</p>
                </div>
              </div>
              <div className="flex items-start gap-5" data-testid="dist-phase-3">
                <p className="font-serif-display text-3xl text-[#C89D3C] leading-none">03</p>
                <div>
                  <p className="font-serif-display text-xl text-[#3B2A1A]">South & Diaspora</p>
                  <p className="text-sm text-[#5C4A3A] mt-1 font-light">Karnataka, TN, Telangana — and select export markets.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#3B2A1A]/10 border border-[#3B2A1A]/10 self-start">
            {POINTS.map((p, i) => (
              <div key={p.t} className="bg-white p-7" data-testid={`dist-point-${i}`}>
                <p className="font-serif-display text-2xl text-[#3B2A1A]">{p.t}</p>
                <p className="text-sm text-[#5C4A3A] mt-2 font-light leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-[#3B2A1A] text-[#FBF6EC]" data-testid="distributor-form-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="overline text-[#E2C37B] mb-5">Express Interest</p>
            <h2 className="font-serif-display text-4xl lg:text-5xl leading-[1.05] tracking-tight">A serious conversation. With the right people.</h2>
          </div>
          <form onSubmit={onSubmit} className="lg:col-span-7 space-y-5" data-testid="distributor-form">
            {[
              { k: "name", l: "Your Name" },
              { k: "company", l: "Company Name" },
              { k: "state", l: "State" },
              { k: "phone", l: "Phone" },
              { k: "territory", l: "Territory Proposed" },
            ].map((f) => (
              <div key={f.k}>
                <label className="text-[10px] uppercase tracking-[0.22em] text-[#E2C37B]">{f.l}</label>
                <input
                  required
                  data-testid={`distributor-input-${f.k}`}
                  value={form[f.k]}
                  onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  className="w-full bg-transparent border-b border-[#FBF6EC]/30 py-3 text-base text-[#FBF6EC] focus:outline-none focus:border-[#C89D3C]"
                />
              </div>
            ))}
            <button type="submit" data-testid="distributor-submit-btn" className="mt-4 inline-flex items-center gap-2 bg-[#C89D3C] hover:bg-[#E2C37B] text-[#3B2A1A] uppercase tracking-[0.18em] text-xs font-semibold px-8 py-4 transition-colors">
              Send Enquiry <ArrowRight size={14} />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Distributor;
